import { ReactElement, useEffect } from 'react'
import { Box, Button, Divider, Link, Typography, alpha } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Link as RouterLink } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { ConfirmIcon, DownloadIcon } from 'src/v3/core/icons'
import {
  certificatePollRequested,
  selectCertificatePollByCourseID,
  selectIsCourseFullyComplete,
  selectIsRestartingByID,
} from 'src/v3/classroom-v2/classroom-v2-wizard.slice'
import { RestartCourseButton } from 'src/v3/classroom-v2/components/restart-course-control'
import { CourseV2Certificate, CourseV2Content } from 'src/v3/classroom-v2/types'

// The last known answer from the poll, whether it has come to rest on it or is
// still asking. Same reasoning as the certificate step's: reading the in-flight
// case from `last` is what stops the panel flickering between a real state and a
// placeholder every couple of seconds.
let lastKnownCertificate = (
  poll: ReturnType<ReturnType<typeof selectCertificatePollByCourseID>>,
): CourseV2Certificate | null => {
  if (poll === null) return null
  return poll.kind === 'answered' ? poll.certificate : poll.last
}

// The download, on the overview. The URL is presigned and short-lived, so this
// renders it ONLY from a freshly-polled record — never from the copy sitting in the
// progress record, which would hand the learner a 403 from S3. That's the whole
// reason this panel polls at all rather than reading the certificate it already has
// in `sessionProgress`.
//
// Until the poll answers, the button is present but inert rather than absent: the
// learner has finished, so a download belongs here, and a control that appears a
// second later moves everything under it.
let CertificateDownload = ({
  certificate,
}: {
  certificate: CourseV2Certificate | null
}): ReactElement =>
  certificate?.kind === 'issued' ? (
    <Button
      variant="contained"
      color="success"
      startIcon={<DownloadIcon />}
      href={certificate.certificate.downloadURL}
      // Straight at S3, so this leaves the app; noopener/noreferrer because
      // target=_blank without them hands the opened page a handle on this window.
      target="_blank"
      rel="noopener noreferrer"
    >
      Download Certificate (PDF)
    </Button>
  ) : (
    <Button variant="contained" color="success" disabled>
      Preparing certificate…
    </Button>
  )

// What the overview says to a learner who has already finished. It exists because
// the page's default answer — a hero inviting you to start — is the wrong thing to
// say to someone who is done, and because the two things they'd come back here for
// are the certificate and another attempt at the credit.
//
// Renders nothing at all until the server's progress record says the course is
// fully complete, so an un-started or in-progress course sees the ordinary overview
// with no extra chrome. selectIsCourseFullyComplete reads sessionProgress and is
// therefore scoped to the current restart boundary for free: the moment a restart's
// fresh projection lands, this panel disappears on its own without anything here
// knowing restart exists.
export let CourseCompletionPanel = ({
  content,
}: {
  content: CourseV2Content
}): ReactElement | null => {
  let theme = useTheme()
  let dispatch = useAppDispatch()
  let { courseID } = content
  let isComplete = useAppSelector(selectIsCourseFullyComplete(courseID))
  let isRestarting = useAppSelector(selectIsRestartingByID(courseID))
  let poll = useAppSelector(selectCertificatePollByCourseID(courseID))

  // Completion IS the cue to poll — there is no user action to wait for, exactly as
  // on the certificate step, and the same null-record guard means this can't restart
  // a loop that's already running or one that has already finished. The cadence and
  // the stopping rule stay in the middleware; this only announces that a certificate
  // is being shown.
  useEffect(() => {
    if (isComplete && poll === null)
      dispatch(certificatePollRequested(courseID))
  }, [isComplete, poll === null, courseID])

  // Held through a restart as well as through the un-started case. Between the wipe
  // and the fresh progress landing there is genuinely no progress record, so
  // isComplete goes false for a beat — without this the panel would vanish and the
  // page would snap back to its un-started self before the new projection arrives.
  if (!isComplete && !isRestarting) return null

  let borderColor = theme.palette.success.main

  // A restart in flight gets its own body rather than the completion one. The wipe
  // takes the certificate poll with it, so the completion body would spend that
  // interval saying "you've completed this course" beside a disabled "preparing
  // certificate" — describing a course that is, by then, no longer complete and a
  // certificate nobody is waiting for. Announced politely so the change of state
  // isn't purely visual.
  if (isRestarting) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          px: 3,
          py: 2.5,
          borderRadius: 2,
          border: `1px solid ${alpha(borderColor, 0.3)}`,
          borderTop: `3px solid ${borderColor}`,
          bgcolor: 'background.paper',
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          Restarting this course…
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Setting up your new attempt. Your previous certificate and CE credit
          are unaffected.
        </Typography>
      </Box>
    )
  }

  let certificate = lastKnownCertificate(poll)

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(borderColor, 0.3)}`,
        borderTop: `3px solid ${borderColor}`,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ px: 3, py: 2.5, display: 'flex', gap: 2 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: alpha(borderColor, 0.15),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <ConfirmIcon fontSize="small" sx={{ color: 'success.main' }} />
        </Box>
        <Box>
          {/* "You've completed this course" and not "you're certified": finishing
              earns a certificate for THIS course, which isn't the same as holding a
              certification. Same line the certificate surface is careful about. */}
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            You&apos;ve completed this course
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your CE certificate has been issued and your hours are recorded.
          </Typography>

          <Box
            sx={{
              mt: 2.5,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <CertificateDownload certificate={certificate} />
            <Typography variant="body2" color="text.secondary">
              Or find it any time in{' '}
              <Link
                component={RouterLink}
                to="/profile/certificates"
                color="primary"
              >
                your certificates
              </Link>
              .
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* The restart offer, in its own region below the divider, because it's a
          different proposition from the certificate above it — not "here's what you
          earned" but "here's how to earn it again". Explained before it's offered:
          a learner who doesn't know a restart resets their assessment and survey
          can't meaningfully consent to one, and the button alone would read as an
          innocent "watch it again". */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          bgcolor: alpha(theme.palette.background.default, 0.5),
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Taking this course again?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Restarting begins a new attempt: your steps, assessment and survey all
          reset, and completing them again earns a new certificate and a new CE
          credit. The certificate you already have is unaffected.
        </Typography>
        <RestartCourseButton
          courseID={courseID}
          courseName={content.name}
          variant="outlined"
          color="primary"
        />
      </Box>
    </Box>
  )
}
